import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useMainStore from "../../../store/mainState";

interface WorldCupPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const WorldCupPopup = ({ isOpen, onClose }: WorldCupPopupProps) => {
  const navigate = useNavigate();
  const { setActiveItem, setActiveSubItem } = useMainStore();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const goToSport = () => {
    setActiveItem("sport");
    setActiveSubItem("");
    navigate("/sport");
    onClose();
  };

  // Confetti particles
  const confetti = Array.from({ length: 30 }, (_, i) => i);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center animate-[wcFadeIn_0.3s_ease-out]">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Popup body */}
      <div
        className="relative m-0 sm:m-[2vw] w-[100vw] sm:w-[92vw] sm:max-w-[560px]
          h-[100vh] sm:h-auto sm:max-h-[92vh] overflow-hidden
          rounded-none sm:rounded-[28px]
          shadow-[0_8px_80px_rgba(0,134,211,0.5),0_2px_20px_rgba(0,0,0,0.6)]
          animate-[wcSlideUp_0.45s_cubic-bezier(0.16,1,0.3,1)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Layered background: stadium night sky → green pitch */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617] via-[#0a1d3c] to-[#022c1a]" />

        {/* Stadium light rays */}
        <div className="absolute top-0 left-0 right-0 h-[55%] overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#4DBDFF]/15 rounded-full blur-[100px]" />
          <div className="absolute top-[10%] left-[10%] w-[180px] h-[300px] bg-white/[0.06] blur-[60px] rotate-[20deg]" />
          <div className="absolute top-[10%] right-[10%] w-[180px] h-[300px] bg-white/[0.06] blur-[60px] -rotate-[20deg]" />
        </div>

        {/* Pitch lines on bottom (grass) */}
        <div className="absolute bottom-0 left-0 right-0 h-[45%] pointer-events-none">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, transparent, transparent 30px, rgba(255,255,255,0.04) 30px, rgba(255,255,255,0.04) 60px)",
            }}
          />
          <div className="absolute bottom-0 left-0 right-0 h-[60%] bg-gradient-to-t from-[#022c1a] to-transparent" />
        </div>

        {/* Confetti */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {confetti.map((i) => {
            const colors = ["#4DBDFF", "#FFD700", "#FF4D6D", "#10B981", "#FFFFFF"];
            const color = colors[i % colors.length];
            const left = (i * 37) % 100;
            const delay = (i % 10) * 0.3;
            const duration = 4 + (i % 5);
            const size = 6 + (i % 4) * 2;
            return (
              <span
                key={i}
                className="absolute top-[-20px] rounded-sm"
                style={{
                  left: `${left}%`,
                  width: `${size}px`,
                  height: `${size * 0.4}px`,
                  background: color,
                  animation: `wcConfetti ${duration}s linear ${delay}s infinite`,
                  opacity: 0.85,
                }}
              />
            );
          })}
        </div>

        {/* Glass border */}
        <div className="absolute inset-0 sm:rounded-[28px] border border-white/[0.08] pointer-events-none" />

        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-5 right-5 z-30 w-[36px] h-[36px] flex items-center justify-center
            rounded-full bg-black/40 border border-white/15
            text-white/70 hover:text-white hover:bg-black/60 hover:border-white/30
            transition-all duration-300 hover:rotate-90"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </button>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center
          px-[20px] sm:px-[36px] py-[40px] sm:py-[44px]
          h-full sm:h-auto overflow-y-auto">

          {/* Top label */}
          <div className="flex items-center gap-2 px-4 py-1.5 rounded-full
            bg-gradient-to-r from-[#0086D3]/30 to-[#4DBDFF]/20
            border border-[#4DBDFF]/40
            animate-[wcPulse_2.5s_ease-in-out_infinite]">
            <span className="w-2 h-2 rounded-full bg-[#FF4D6D] animate-pulse" />
            <span className="text-[11px] sm:text-[12px] font-bold tracking-[0.2em] text-white uppercase">
              Live Now · Limited Time
            </span>
          </div>

          {/* Host flags */}
          <div className="flex items-center gap-2 mt-5 text-[24px] sm:text-[28px] animate-[wcFloat_3s_ease-in-out_infinite]">
            <span title="USA">🇺🇸</span>
            <span title="Canada">🇨🇦</span>
            <span title="Mexico">🇲🇽</span>
          </div>

          {/* FIFA-style title */}
          <h2 className="mt-3 text-center font-black italic tracking-tight leading-[0.95]
            text-[44px] sm:text-[58px]
            bg-clip-text text-transparent
            bg-gradient-to-b from-white via-[#E0F2FF] to-[#4DBDFF]
            drop-shadow-[0_4px_30px_rgba(77,189,255,0.4)]">
            WORLD CUP
          </h2>

          {/* Big 2026 with shimmer */}
          <div className="relative mt-1">
            <h1 className="text-center font-black italic
              text-[78px] sm:text-[110px] leading-[0.9]
              bg-clip-text text-transparent
              bg-gradient-to-b from-[#FFD700] via-[#FFA500] to-[#FF6B00]
              drop-shadow-[0_6px_40px_rgba(255,165,0,0.5)]">
              2026
            </h1>
            <div className="absolute inset-0 pointer-events-none animate-[wcShimmer_3s_ease-in-out_infinite]
              bg-gradient-to-r from-transparent via-white/40 to-transparent
              bg-clip-text text-transparent
              text-[78px] sm:text-[110px] font-black italic text-center leading-[0.9]">
              2026
            </div>
          </div>

          {/* Soccer ball + trophy row */}
          <div className="relative flex items-center justify-center gap-8 mt-2 mb-4">
            {/* Spinning ball */}
            <div className="relative w-[68px] h-[68px] sm:w-[80px] sm:h-[80px] flex items-center justify-center">
              <div className="absolute inset-0 bg-white/25 blur-2xl rounded-full" />
              <div className="absolute inset-[6px] bg-[#4DBDFF]/30 blur-xl rounded-full" />
              <span
                className="relative inline-block text-[56px] sm:text-[68px] leading-none
                  animate-[wcSpin_4s_linear_infinite]
                  drop-shadow-[0_4px_18px_rgba(255,255,255,0.55)]"
              >
                ⚽
              </span>
            </div>

            {/* Trophy */}
            <div className="relative w-[68px] h-[68px] sm:w-[80px] sm:h-[80px] flex items-center justify-center">
              <div className="absolute inset-0 bg-[#FFD700]/35 blur-2xl rounded-full" />
              <span
                className="relative inline-block text-[56px] sm:text-[68px] leading-none
                  animate-[wcFloat_3s_ease-in-out_infinite]
                  drop-shadow-[0_4px_24px_rgba(255,215,0,0.6)]"
              >
                🏆
              </span>
            </div>
          </div>

          {/* Subtitle pitch */}
          <p className="text-center text-white/85 text-[14px] sm:text-[16px] font-medium max-w-[420px] leading-relaxed px-2">
            The biggest tournament on earth is here.
            <br />
            <span className="text-[#FFD700] font-bold">Boosted odds</span>,
            live betting & <span className="text-[#4DBDFF] font-bold">exclusive promos</span> on every match.
          </p>

          {/* CTA */}
          <button
            onClick={goToSport}
            className="relative mt-7 group w-full max-w-[360px] overflow-hidden
              rounded-full py-[16px] sm:py-[18px]
              bg-gradient-to-r from-[#FF4D6D] via-[#FFA500] to-[#FFD700]
              shadow-[0_8px_30px_rgba(255,77,109,0.5),inset_0_1px_0_rgba(255,255,255,0.4)]
              transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_10px_40px_rgba(255,165,0,0.7),inset_0_1px_0_rgba(255,255,255,0.5)]
              active:scale-[0.98]"
          >
            {/* Sweep shine */}
            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full
              transition-transform duration-1000
              bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            <span className="relative flex items-center justify-center gap-2
              text-white font-black uppercase tracking-wider
              text-[15px] sm:text-[17px] drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
              Bet on World Cup 2026
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                className="transition-transform duration-300 group-hover:translate-x-1">
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>

          {/* Tiny disclaimer */}
          <button
            onClick={onClose}
            className="mt-4 text-[12px] text-white/45 hover:text-white/80 transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes wcFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes wcSlideUp {
          from { opacity: 0; transform: translateY(40px) scale(0.94); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes wcSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes wcFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes wcPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(77,189,255,0.4); }
          50% { box-shadow: 0 0 0 8px rgba(77,189,255,0); }
        }
        @keyframes wcShimmer {
          0% { transform: translateX(-100%); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
        @keyframes wcConfetti {
          0% {
            transform: translateY(-20px) rotate(0deg);
            opacity: 0;
          }
          10% { opacity: 0.9; }
          100% {
            transform: translateY(120vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default WorldCupPopup;
